import { AuthClient } from '@appcues/auth';
import { createClient as createWSClient, Client as WSClient } from 'graphql-ws';

const MAX_RETRIES = 5;

export type WebSocketClient = {
  client: WSClient;
  connect: () => Promise<void>;
  disconnect: () => void;
};

export function createWebSocketClient(
  auth: AuthClient,
  websocketUrl = import.meta.env.CUSTOMER_API_WEBSOCKET_URL
): WebSocketClient {
  let connectPromise: Promise<void> | null = null;
  let isConnected = false;

  const wsClient = createWSClient({
    url: `${websocketUrl}/v1/graphql-ws/websocket`,
    retryAttempts: MAX_RETRIES,
    shouldRetry: () => true,
    connectionParams: async () => ({
      Authorization: `Bearer ${await auth.getToken()}`,
    }),
    on: {
      connected: () => {
        isConnected = true;
      },
      closed: (event: unknown) => {
        isConnected = false;
        connectPromise = null;

        // Handle close events gracefully to prevent CloseEvent errors
        if (event && typeof event === 'object' && 'code' in event) {
          const closeEvent = event as { code: number; reason?: string };
          // Only log non-normal close events (1000 = normal closure, 1001 = going away)
          if (closeEvent.code !== 1000 && closeEvent.code !== 1001) {
            console.debug(
              'WebSocket closed:',
              closeEvent.code,
              closeEvent.reason
            );
          }
        }
      },
      error: error => {
        console.error('WebSocket error:', error);
        isConnected = false;
        connectPromise = null;
      },
    },
  });

  const connect = () => {
    if (isConnected) {
      return Promise.resolve();
    }

    if (!connectPromise) {
      connectPromise = new Promise<void>((resolve, reject) => {
        const cleanupConnected = wsClient.on('connected', () => {
          cleanupConnected();
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          cleanupError();
          resolve();
        });

        const cleanupError = wsClient.on('error', error => {
          console.error('WebSocket error in connect:', error);
          cleanupConnected();
          cleanupError();
          reject(error);
        });
      });
    }

    return connectPromise;
  };

  const disconnect = () => {
    if (isConnected) {
      isConnected = false;
      connectPromise = null;
      wsClient.dispose();
    }
  };

  return {
    client: wsClient,
    connect,
    disconnect,
  };
}
